// Client-side geocoding for the Customer Map page. No backend involved —
// addresses are turned into coordinates via LocationIQ (a hosted, rate-
// limit-friendly Nominatim/OSM engine), then cached in localStorage so
// repeat page loads don't re-hit it.
//
// LocationIQ sits on the same underlying OSM map data as plain Nominatim, so
// it has the same real gaps in AU house-number coverage — some addresses
// only resolve to the street centerline, not the actual house, because OSM
// has no house-number node for that property. What LocationIQ does better
// (confirmed against real customer addresses) is tolerate messy input —
// e.g. "Unit 47 Percival Court, 19 Howitt Street, Kingston" — where plain
// Nominatim's parser gave up entirely and needed the unit/building name
// stripped out to match anything.
//
// We also saw LocationIQ (like any Nominatim-based engine) occasionally
// substitute a similarly-named street in the WRONG suburb and still report
// a house_number — confidently wrong, not just imprecise. So a result is
// only trusted as "precise" when it has a house_number AND its postcode
// matches what's on file for the customer.
//
// Requires VITE_LOCATIONIQ_API_KEY (admin-portal/.env). Free tier caps
// unauthenticated-feeling abuse at ~2 requests/second — we queue requests
// sequentially with a delay between them regardless, both to respect that
// and to keep behavior correct if the customer list grows.

const LOCATIONIQ_URL = 'https://us1.locationiq.com/v1/search';
const CACHE_KEY = 'customerMapGeocodeCacheV4'; // v4 = LocationIQ-sourced + postcode-checked; don't mix with older versions
const MIN_REQUEST_GAP_MS = 550;
const API_KEY = import.meta.env.VITE_LOCATIONIQ_API_KEY;

let warnedMissingKey = false;
let lastRequestAt = 0;
let queue = Promise.resolve();

const readCache = () => {
  try {
    return JSON.parse(localStorage.getItem(CACHE_KEY)) || {};
  } catch {
    return {};
  }
};

const writeCache = (cache) => {
  try {
    localStorage.setItem(CACHE_KEY, JSON.stringify(cache));
  } catch {
    // localStorage full/unavailable — geocoding still works, just uncached.
  }
};

// Builds a cache key + the query variants to try, in order of preference,
// from whatever address data the customer record has. Newer records carry
// street/suburb/state/postcode; older ones only have a combined `address`
// string (which may include unit/building info LocationIQ handles fine as
// free text but structured fields don't have a slot for). Some records have
// both (legacy `address` migrated alongside newer structured fields) — try
// every source available rather than picking one and ignoring the rest, so
// a badly-formatted `address` string doesn't shadow a perfectly good
// structured fallback.
const addressPlan = (customer) => {
  const { street, suburb, state, postcode, address } = customer;
  const structuredLine = [street, suburb, state, postcode].filter(Boolean).join(', ');
  if (!address && !structuredLine) return null;

  const variants = [
    // Free text first — tolerates unit/building-name noise (e.g. in a legacy
    // `address` string, or a `street` field with a unit prefix) better than
    // structured fields do.
    address && { q: address },
    structuredLine !== address && { q: structuredLine },
    // Structured, most specific.
    { street, city: suburb, state, postalcode: postcode },
    // Postcode boundaries in OSM sometimes don't line up with the real one
    // for a given street; dropping it can unblock a match.
    { street, city: suburb, state },
  ].filter((v) => v && Object.values(v).some(Boolean));

  return { key: (address || structuredLine).toLowerCase(), postcode, variants };
};

// Lets callers distinguish "no address on file" from "address didn't
// geocode at all" when explaining why a customer isn't plotted on the map.
export const hasAddress = (customer) => !!addressPlan(customer);

const throttledFetch = (url) => {
  const run = async () => {
    const wait = Math.max(0, MIN_REQUEST_GAP_MS - (Date.now() - lastRequestAt));
    if (wait > 0) await new Promise((r) => setTimeout(r, wait));
    lastRequestAt = Date.now();
    return fetch(url);
  };
  // Chain onto the shared queue so calls stay sequential regardless of how
  // many geocodeAddress() calls are in flight, and regardless of how many
  // variants a single address needs to try.
  const result = queue.then(run);
  queue = result.catch(() => {});
  return result;
};

const runQuery = async (variant) => {
  const params = new URLSearchParams({
    key: API_KEY,
    // LocationIQ (unlike Nominatim) rejects format=jsonv2 with a 400 —
    // "json" is the format it actually supports; it still returns
    // addressdetails.house_number/postcode the same way.
    format: 'json',
    limit: '1',
    addressdetails: '1',
    countrycodes: 'au',
    ...variant,
  });
  const res = await throttledFetch(`${LOCATIONIQ_URL}?${params.toString()}`);
  if (!res.ok) {
    console.warn(`[CustomerMap] LocationIQ request failed: ${res.status} ${res.statusText}`);
    return null;
  }
  const data = await res.json();
  if (data?.error) return null; // LocationIQ returns {error: "..."} (still HTTP 200) on no-match
  return Array.isArray(data) ? data[0] || null : null;
};

// A result only counts as "precise" when it has a house_number AND, if we
// have a postcode on file, that postcode matches — catches the case where
// the engine confidently substitutes a similarly-named street in the wrong
// suburb (seen in testing) instead of admitting no match.
const isPrecise = (match, expectedPostcode) => {
  if (!match?.address?.house_number) return false;
  if (expectedPostcode && match.address.postcode && match.address.postcode !== expectedPostcode) return false;
  return true;
};

// Resolves a customer's address to { lat, lon, precise }, or null if
// nothing could be found at all.
export const geocodeAddress = async (customer) => {
  const plan = addressPlan(customer);
  if (!plan) return null;

  // Coordinates stored on the customer row win (backfilled server-side by
  // scripts/backfill-customer-geocodes.cjs) — no network needed. geoAddressKey
  // is the fingerprint of the address that was geocoded; if the address on
  // file has since changed the key won't match and we fall through to live
  // client-side geocoding until the backfill is re-run.
  if (customer.geoLat != null && customer.geoLng != null && customer.geoAddressKey === plan.key) {
    return { lat: customer.geoLat, lon: customer.geoLng, precise: !!customer.geoPrecise };
  }

  const cache = readCache();
  if (Object.prototype.hasOwnProperty.call(cache, plan.key)) {
    return cache[plan.key];
  }

  if (!API_KEY) {
    if (!warnedMissingKey) {
      console.warn(
        '[CustomerMap] VITE_LOCATIONIQ_API_KEY is not set — customers cannot be geocoded. ' +
        'Add it to admin-portal/.env (see .env.example).'
      );
      warnedMissingKey = true;
    }
    return null;
  }

  let fallback = null;

  for (const variant of plan.variants) {
    let match;
    try {
      match = await runQuery(variant);
    } catch {
      continue;
    }
    if (!match) continue;

    const coords = { lat: Number(match.lat), lon: Number(match.lon) };

    if (isPrecise(match, plan.postcode)) {
      const result = { ...coords, precise: true };
      const nextCache = readCache();
      nextCache[plan.key] = result;
      writeCache(nextCache);
      return result;
    }
    if (!fallback) fallback = coords;
  }

  const result = fallback ? { ...fallback, precise: false } : null;
  if (result) {
    const nextCache = readCache();
    nextCache[plan.key] = result;
    writeCache(nextCache);
  }
  return result;
};
